var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { P, CustomElement, Watch, PacemEventTarget, Components, Utils, PropertyConverters, Logging } from '@pacem/pacem-core';
//namespace Pacem.Components.UI {
const ANIM_TIMEOUT = 500;
const ElementOrStringPropertyConverter = {
    convert: (attr, element) => {
        try {
            return document.querySelector(attr) ?? (element.isConnected ? attr : null);
        }
        catch {
            return attr;
        }
    },
    convertBack: (prop) => (prop instanceof Element && prop.id) ? ('#' + prop.id) : prop,
    retryConversionWhenReady: true
};
let PacemViewElement = class PacemViewElement extends PacemEventTarget {
    constructor() {
        super(...arguments);
        this._templateChangeHandler = (evt) => {
            const tmplProxy = evt.target;
            this._setTmpl(tmplProxy.target);
        };
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        // url
        if (name === 'url' || name === 'disabled') {
            this._dispose(old);
            this._manage();
        }
        else if (name === 'fetching') {
            (val ? Utils.addClass : Utils.removeClass)(this, 'view-fetching');
        }
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        const url = this.url;
        if (this._isReference(url) && !this.disabled) {
            this._manageRef();
        }
    }
    _isReference(url) {
        return url instanceof HTMLElement || (typeof url === 'string' && url.trim().startsWith('#'));
    }
    _dispose(url) {
        if (url instanceof Components.PacemTemplateProxyElement) {
            url.removeEventListener('templatechange', this._templateChangeHandler);
        }
    }
    _manage() {
        if (this.disabled) {
            return;
        }
        const debounce = this.debounce || 0, url = this.url;
        ;
        if (this._isReference(url)) {
            // <template>-based
            clearTimeout(this._fetchHandle);
            this._fetchHandle = setTimeout((() => this._manageRef()), debounce);
        }
        else if (typeof url === 'string' && !Utils.isNullOrEmpty(url)) {
            // url-based
            this.fetching = true;
            clearTimeout(this._fetchHandle);
            this._fetchHandle = setTimeout((() => this._manageUrl(url)), debounce);
        }
        else /* might be a reset */ {
            this.innerHTML = '';
        }
    }
    _manageRef() {
        const url = this.url, tmpl = url instanceof HTMLElement ? url : document.querySelector('template' + url);
        if (tmpl instanceof Components.PacemTemplateProxyElement) {
            tmpl.addEventListener('templatechange', this._templateChangeHandler, false);
            this._setTmpl(tmpl.target);
        }
        else if (tmpl instanceof HTMLTemplateElement) {
            this._setTmpl(tmpl);
        }
        else if (!Utils.isNull(tmpl)) {
            this.log(Logging.LogLevel.Error, `Invalid '${tmpl.constructor.name}' element, HTMLTemplate expected.`);
        }
        else {
            this.log(Logging.LogLevel.Error, `Template element not found.`);
        }
    }
    _setTmpl(tmpl) {
        cancelAnimationFrame(this._renderHandle);
        // this way, `this._container.innerHTML = ''` instruction will eventually let the page "scroll up"...
        this.innerHTML = '';
        this._renderHandle = requestAnimationFrame(() => {
            if (Utils.isNull(tmpl)) {
                this.log(Logging.LogLevel.Error, `Template element not found.`);
                return;
            }
            this.appendChild(tmpl.cloneNode(true).content);
            this._dispatchRender();
        });
    }
    #aborter;
    _manageUrl(url) {
        if (this.#aborter) {
            this.#aborter.abort();
        }
        const { signal } = this.#aborter = new AbortController();
        fetch(url, { credentials: 'same-origin', signal }).then(async (r) => {
            // inline function
            const manageYield = (r, htmlPromise) => {
                if ((r.status === 301 || r.status === 302) && this.followRedirects) {
                    this._manageUrl(r.headers.get("Location"));
                }
                else if (r.ok || this.renderErrors) {
                    htmlPromise.then((html) => Utils.addAnimationEndCallback(this, () => this._manageResult(html), ANIM_TIMEOUT));
                }
                // fallback to empty content.
                else
                    this._manageResult('');
            };
            const contentLength = +r.headers.get('Content-Length');
            if (contentLength > 0) {
                this.progress = 0;
                let received = 0;
                const chunks = [];
                // progress available
                const reader = r.body.getReader();
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        break;
                    }
                    this.progress = (received += value.length) / contentLength;
                    chunks.push(value);
                }
                manageYield(r, new Promise((resolve, reject) => {
                    try {
                        let allChunks = new Uint8Array(received);
                        let position = 0;
                        for (let chunk of chunks) {
                            allChunks.set(chunk, position);
                            position += chunk.length;
                        }
                        const html = new TextDecoder("utf-8").decode(allChunks);
                        resolve(html);
                    }
                    catch (e) {
                        reject(e);
                    }
                }));
            }
            else {
                manageYield(r, r.text());
            }
        }, _ => {
            // do nothing on fail
        });
    }
    _manageResult(result) {
        // this way, `this._container.innerHTML = ''` instruction will eventually let the page "scroll up"...
        cancelAnimationFrame(this._renderHandle);
        this.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        this.innerHTML = '';
        this._renderHandle = requestAnimationFrame(() => {
            this.innerHTML = result;
            this.fetching = false;
            this.progress = Number.NaN;
            // setting fetching to `false` triggers a css class change that could/should cause a css transition to happen
            // TODO: manage to switch to native in a near future (https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API or https://youtu.be/oDcb3fvtETs)
            Utils.addAnimationEndCallback(this, () => {
                this._dispatchRender();
            }, ANIM_TIMEOUT);
        });
    }
    _dispatchRender() {
        this.dispatchEvent(new CustomEvent("render"));
    }
};
__decorate([
    Watch({ reflectBack: true, converter: ElementOrStringPropertyConverter })
], PacemViewElement.prototype, "url", void 0);
__decorate([
    Watch()
], PacemViewElement.prototype, "fetching", void 0);
__decorate([
    Watch()
], PacemViewElement.prototype, "progress", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Boolean })
], PacemViewElement.prototype, "renderErrors", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Boolean })
], PacemViewElement.prototype, "followRedirects", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemViewElement.prototype, "debounce", void 0);
PacemViewElement = __decorate([
    CustomElement({
        tagName: P + '-view'
    })
], PacemViewElement);
export { PacemViewElement };
